import { logAmplitudeEvent } from 'amplitude/amplitude'
import type { StudioSystemType } from 'types/studio/items'

import type { MountingCalcResult } from '../nativeMounting/types'
import {
  getVanDerValkBomFrameFromSystem,
  getVanDerValkMiddleZoneDeflectorForBom,
  isVanDerValkSolarRampSelectedOnSystem,
} from './fetchVanDerValkMountingBom'
import type { VanDerValkMountingTabBomIssue } from './getVanDerValkMountingTabBomIssue'
import type { VanDerValkSuitabilityConfig } from './types'
import { getVanDerValkModuleGridsForBom, groupVanDerValkModuleGridsByFacet } from './vanDerValkModuleGrids'

export type VanDerValkPitchedBomSuccessContext = {
  roofType?: string | null
  claddingType?: string | null
}

export type VanDerValkFlatRoofBomSuccessContext = {
  roofType?: string | null
}

type VanDerValkIntegrationJson = {
  selectedSuitability?: unknown
  flatRoofFixation?: string
  anchorType?: string
}

function getVanDerValkIntegrationJson(system: StudioSystemType): VanDerValkIntegrationJson | undefined {
  const raw = (system.integration_json as { vanDerValk?: unknown } | undefined)?.vanDerValk
  if (raw == null || typeof raw !== 'object') {
    return undefined
  }
  return raw as VanDerValkIntegrationJson
}

function getSelectedSuitabilityRow(system: StudioSystemType): VanDerValkSuitabilityConfig | null {
  const raw = getVanDerValkIntegrationJson(system)?.selectedSuitability
  if (raw == null || typeof raw !== 'object') {
    return null
  }
  return raw as VanDerValkSuitabilityConfig
}

export function countVanDerValkActivePanels(system: StudioSystemType): number {
  return getVanDerValkModuleGridsForBom(system).reduce((sum, grid) => {
    const cells = grid.cellsActive
    return sum + (Array.isArray(cells) ? cells.length : 0)
  }, 0)
}

export function vanDerValkMountingTabBomIssueErrorType(issue: VanDerValkMountingTabBomIssue): string {
  if (issue.kind === 'outline') {
    return 'outline'
  }
  if (issue.kind === 'roof_structure') {
    return 'roof_structure'
  }
  return 'missing_inputs'
}

export function trackVanDerValkPitchedBomSuccess(
  result: MountingCalcResult,
  system: StudioSystemType,
  context: VanDerValkPitchedBomSuccessContext
): void {
  if (system.mounting_type !== 'onRoof') {
    return
  }
  const row = getSelectedSuitabilityRow(system)
  const facetCount = groupVanDerValkModuleGridsByFacet(getVanDerValkModuleGridsForBom(system)).size
  logAmplitudeEvent('selected_native_mounting_system', {
    integration: 'vanDerValk',
    mounting_type: 'onRoof',
    systemId: system.uuid,
    itemCount: result.items.length,
    panelCount: countVanDerValkActivePanels(system),
    orientation: row?.moduleOrientation,
    roofType: context.roofType ?? undefined,
    claddingType: context.claddingType ?? undefined,
    facetCount,
    system: row?.system,
    fixation: row?.fixation,
    longProfile: row?.longProfile,
    anchorType: row?.anchorType,
  })
}

export function trackVanDerValkPitchedError(payload: {
  errorType: string
  error_msg: string
  claddingType?: string | null
}): void {
  logAmplitudeEvent('integrated_racking_dialog_error', {
    integration: 'vanDerValk',
    mounting_type: 'onRoof',
    errorType: payload.errorType,
    error_msg: payload.error_msg,
    ...(payload.claddingType ? { claddingType: payload.claddingType } : {}),
  })
}

export function trackVanDerValkFlatRoofBomSuccess(
  result: MountingCalcResult,
  system: StudioSystemType,
  context: VanDerValkFlatRoofBomSuccessContext
): void {
  if (system.mounting_type === 'onRoof') {
    return
  }
  const row = getSelectedSuitabilityRow(system)
  const vd = getVanDerValkIntegrationJson(system)
  const bomFrame = getVanDerValkBomFrameFromSystem(system)
  const solarRamp = isVanDerValkSolarRampSelectedOnSystem(system)
  const middleZoneDeflector = bomFrame ? getVanDerValkMiddleZoneDeflectorForBom(system, bomFrame) : undefined
  logAmplitudeEvent('selected_native_mounting_system', {
    integration: 'vanDerValk',
    mounting_type: 'flatRoof',
    systemId: system.uuid,
    itemCount: result.items.length,
    panelCount: countVanDerValkActivePanels(system),
    orientation: row?.moduleOrientation,
    roofType: context.roofType ?? undefined,
    system: row?.system,
    placeSystem: row?.placeSystem,
    rowPitch: bomFrame?.rowPitch ?? row?.rowPitch,
    middleZoneDeflector,
    solarRamp,
    ...(solarRamp
      ? {
          flatRoofFixation: vd?.flatRoofFixation,
          anchorType: vd?.anchorType,
        }
      : {}),
  })
}

export function trackVanDerValkFlatRoofError(payload: {
  errorType: string
  error_msg: string
}): void {
  logAmplitudeEvent('integrated_racking_dialog_error', {
    integration: 'vanDerValk',
    mounting_type: 'flatRoof',
    errorType: payload.errorType,
    error_msg: payload.error_msg,
  })
}
